import {
  Button,
  DatePicker,
  Dropdown,
  ErrorContainer,
  InputField,
  LabelComponent,
  ModalPopup,
  Stack,
  Title
} from 'components';
import { TextAreaInputField } from 'components/inputField/textAreaInpuField';
import dayjs from 'dayjs';
import { useFormik } from 'formik';
import { getLsUserRoleObject } from 'modules/networkTools/localStorageTokens';
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { getClientNotes } from 'store/clientProfile/clientProfileActions';
import {
  getAllManagers,
  getAllCHWs,
  postUpdateNewManagerReview
} from 'store/clients/clientsActions';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import * as yup from 'yup';

import Avatar from '../screener/components/avatar';
import style from './newManagerReview.module.scss';

const ManagerReviewSchema = yup.object().shape({
  reviewDate: yup.string().required('Date is required.'),
  timeSpent: yup.string().required('Time Spent is required.'),
  manager: yup.string().required('Manager is required.'),
  chw: yup.string().required('CHW is required.'),
  documentationTime: yup.string().required('Documentation time is required.'),
  note: yup.string().required('Note is required.')
});

const ProviderSchema = yup.object().shape({
  managerReviewProvider: yup.string().required('Provider is required.')
});

const useGetNotes = () => {
  const { clientId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const { size, page } = useAppSelector((state) => state.clientProfile.clientProfileList);
  const noteTypes = searchParams.get('noteTypes') ?? '';

  return () => dispatch(getClientNotes({ clientId, page, size, types: noteTypes }));
};

const NewManagerReview = () => {
  const { clientId = '' } = useParams();
  const dispatch = useAppDispatch();
  const managerReview = useAppSelector((state) => state.clients.client?.data?.managerReview);
  const isBillingEnabled = useAppSelector((state) => state.clients.client?.data?.isBillingEnabled);
  const chwId = useAppSelector((state) => state.clients.client?.data?.chwId);
  const [open, setOpen] = useState(false);
  const getNotes = useGetNotes();
  const managers = useAppSelector((state) => state.clients.managers.data);
  const chws = useAppSelector((state) => state.clients.chws.data);
  const userRole = getLsUserRoleObject();
  const userId = userRole.userId;

  const chw = chws.find((c) => c.id.toString() === chwId?.toString());
  const currentManager = managers.find((manager) => manager.id.toString() === userId.toString());

  console.log({ userId, chwId });

  useEffect(() => {
    dispatch(getAllManagers());
    dispatch(getAllCHWs());
  }, []);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      reviewDate: '',
      timeSpent: '10',
      manager: currentManager?.id.toString() || '',
      chw: chw?.id.toString() || '',
      managerReviewProvider: '',
      documentationTime: '1',
      note: "CHW reviewed client's applicable history, changes since last staffing, goals, and interventions to be used to help the client achieve next steps with supervisor."
    },
    validateOnMount: true,
    validationSchema: isBillingEnabled
      ? ManagerReviewSchema.concat(ProviderSchema)
      : ManagerReviewSchema,
    onSubmit: async (values, helpers) => {
      await dispatch(
        postUpdateNewManagerReview({
          reviewDate: values.reviewDate,
          timeSpent: values.timeSpent,
          manager: values.manager,
          chw: values.chw,
          managerReviewProvider: values.managerReviewProvider,
          documentationTime: values.documentationTime,
          note: values.note,
          clientId
        })
      );

      await getNotes();

      helpers.resetForm();

      setOpen(false);
    }
  });

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setOpen(false);
  };

  return (
    <>
      <Button
        size="extraSmall"
        handleOnClick={handleOpen}
        text="Manager Review"
        theme="transparent"
        customStyleClass={style.managerReview}
      />
      <ModalPopup customClassInsideContainer={style.modal} open={open}>
        <Stack width="100%">
          <form onSubmit={formik.handleSubmit}>
            <Stack width="100%" display="flex" flexDirection="column" gap="16px">
              <Stack
                width="100%"
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Title type="h3" text="Manager Review" />
                <button className={style.closeIcon} onClick={handleClose}>
                  <span className="material-icons">close</span>
                </button>
              </Stack>
              <Stack display="flex" flexDirection="column" gap="4px">
                <Stack display="flex" flexDirection="row" gap="16px">
                  <Stack width="50%">
                    <LabelComponent text="Date" mandatory />
                    <DatePicker
                      value={formik.values.reviewDate}
                      onChange={(date: string) => {
                        formik.setFieldValue('reviewDate', date);
                      }}
                      onBlur={() => formik.setFieldTouched('reviewDate', true)}
                    />
                    <ErrorContainer
                      visible={formik.touched.reviewDate && formik.errors.reviewDate}
                      errors={formik.errors}
                      name="reviewDate"
                    />
                  </Stack>
                  <Stack width="50%">
                    <LabelComponent text="Time Spent (min)" mandatory />
                    <InputField
                      value={formik.values.timeSpent}
                      onChange={(e) => {
                        // only allow numbers
                        if (e.target.value.match(/^[0-9]*$/)) {
                          formik.handleChange(e);
                        }
                      }}
                      onBlur={formik.handleBlur}
                      placeholder="0"
                      name="timeSpent"
                    />
                    <ErrorContainer
                      visible={formik.touched.timeSpent && formik.errors.timeSpent}
                      errors={formik.errors}
                      name="timeSpent"
                    />
                  </Stack>
                </Stack>
                <Stack width="100%" display="flex" flexDirection="row" gap="16px">
                  <Stack width="50%">
                    <LabelComponent text="Manager" mandatory />
                    <Dropdown
                      label="Select"
                      data={managers}
                      value={formik.values.manager}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name="manager"
                    />
                    <ErrorContainer
                      visible={formik.touched.manager && formik.errors.manager}
                      errors={formik.errors}
                      name="manager"
                    />
                  </Stack>
                  <Stack width="50%">
                    <LabelComponent text="CHW" mandatory />
                    <Dropdown
                      label="Select"
                      data={chws}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      name="chw"
                      value={formik.values.chw}
                    />
                    <ErrorContainer
                      visible={formik.touched.chw && formik.errors.chw}
                      errors={formik.errors}
                      name="chw"
                    />
                  </Stack>
                </Stack>
                <Stack width="100%" display="flex" flexDirection="row" gap="16px">
                  <Stack width="50%">
                    <LabelComponent text="Provider" />
                    <Dropdown
                      data={managers}
                      label="Select"
                      name="managerReviewProvider"
                      value={formik.values.managerReviewProvider}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                    />
                    <ErrorContainer
                      errors={formik.errors}
                      visible={
                        formik.touched.managerReviewProvider && formik.errors.managerReviewProvider
                      }
                      name="managerReviewProvider"
                    />
                  </Stack>
                  <Stack width="50%">
                    <LabelComponent text="Documentation Time (min)" mandatory />
                    <InputField
                      placeholder="0"
                      name="documentationTime"
                      value={formik.values.documentationTime}
                      onChange={(e) => {
                        // only allow numbers
                        if (e.target.value.match(/^[0-9]*$/)) {
                          formik.handleChange(e);
                        }
                      }}
                      onBlur={formik.handleBlur}
                    />
                    <ErrorContainer
                      errors={formik.errors}
                      visible={formik.touched.documentationTime && formik.errors.documentationTime}
                      name="documentationTime"
                    />
                  </Stack>
                </Stack>
                <Stack width="100%">
                  <LabelComponent text="Note" mandatory />
                  <TextAreaInputField
                    rowsNumber={4}
                    placeholder="Type here"
                    value={formik.values.note}
                    name="note"
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  <ErrorContainer
                    errors={formik.errors}
                    visible={formik.touched.note && formik.errors.note}
                    name="note"
                  />
                </Stack>
              </Stack>
              <Stack display="flex" flexDirection="row" justifyContent="space-between">
                {managerReview?.lastReviewDate && (
                  <Stack
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    gap="8px"
                    flexGrow={1}
                  >
                    <p className={style.lastReview}>
                      Last review: {dayjs(managerReview.lastReviewDate).format('MM/DD/YYYY')}
                    </p>
                    <Stack
                      className={style.author}
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      gap="4px"
                    >
                      <Avatar imageUrl={managerReview.authorImage} />
                      <p>{managerReview.authorName}</p>
                    </Stack>
                  </Stack>
                )}
                <Stack width="128px">
                  <Button disabled={!formik.isValid} theme="primary" text="Final" type="submit" />
                </Stack>
              </Stack>
            </Stack>
          </form>
        </Stack>
      </ModalPopup>
    </>
  );
};

export default NewManagerReview;
